/**
 * DijitalRozetSkillPicker — Dijital Rozet beceri seçici.
 *
 * Ayrı bileşen olmasının sebebi: AdminCourseManager gibi paylaşımlı sayfalara
 * tek satır import + tek JSX bloğu ile girsin, o dosyalarda çakışma yaratmasın.
 *
 * Kütüphane hakkında bilinmesi gerekenler (arayüzde de yazıyor):
 *   - Kaynak Lightcast/EMSI taksonomisi; Türkçe karşılıklar makine çevirisi.
 *   - Arama düz metin eşleşmesi yapar, anlamsal değil ("uçuş" → "Sunucusu" getirir).
 *   - Türkçe indeks İngilizceden zayıf ("ilk yardım" 0, "first aid" 10 sonuç).
 *   - Karşılığı bulunamayan beceri "özel beceri" olarak eklenebilir (skillId boş).
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useSkillSearch } from '../../api/admin/dijitalRozet';
import { getApiErrorMessage } from '../../utils/apiError';

/** Havacılık/eğitim tarafında karşılığı doğrulanmış kütüphane becerileri. */
const ONERILEN_BECERILER = [
  { skillId: 'KS120CF6WLXS5QGSWHR9', title: 'Aviation', languageCode: 'en' },
  { skillId: 'KS120TS6X5KKMLFLY2LX', title: 'Aviation Flight', languageCode: 'en' },
  { skillId: 'KS120TW5XGX5PVX0NG33', title: 'Flight Training', languageCode: 'en' },
  { skillId: 'KS1200X6V3937RSF8VVM', title: 'First Aid', languageCode: 'en' },
  { skillId: 'KS121Z26S4VJLQ1WXN21', title: 'Customer Service', languageCode: 'en' },
  { skillId: 'KS127L66QFT04NF9BD1D', title: 'Yolcu Hizmeti', languageCode: 'tr' },
  { skillId: 'KS121B45VQ9R9GK6BV97', title: 'Kabin Basıncı', languageCode: 'tr' },
  { skillId: 'KS124VL76F1D07MLLMVL', title: 'Human Resources Concepts', languageCode: 'en' },
  { skillId: 'KS1218P5Y0HGBD3Z4L3Q', title: 'İş İngilizcesi', languageCode: 'tr' },
  { skillId: 'KS1217P66NK6BW72M9FH', title: 'Müşteri İlişkileri Yönetimi', languageCode: 'tr' },
];

/** Aynı beceri iki kez eklenmesin: skillId varsa ona, yoksa başlığa bakılır. */
function ayniMi(a, b) {
  if (a.skillId && b.skillId) return a.skillId === b.skillId;
  return a.title.trim().toLowerCase() === b.title.trim().toLowerCase();
}

export default function DijitalRozetSkillPicker({ value = [], onChange, disabled = false }) {
  const [term, setTerm] = useState('');
  const [lang, setLang] = useState('tr');
  const [aramaAktif, setAramaAktif] = useState(false);
  const [ozelBaslik, setOzelBaslik] = useState('');

  const { data, isFetching, error } = useSkillSearch(term, lang, aramaAktif);
  const sonuclar = data?.results || [];

  const ekle = (skill) => {
    if (disabled) return;
    if (value.some((s) => ayniMi(s, skill))) return;
    onChange([...value, skill]);
  };

  const cikar = (skill) => {
    if (disabled) return;
    onChange(value.filter((s) => !ayniMi(s, skill)));
  };

  const ozelEkle = () => {
    const baslik = ozelBaslik.trim();
    if (!baslik) return;
    ekle({ skillId: '', title: baslik, languageCode: lang });
    setOzelBaslik('');
  };

  const ara = () => {
    if (!term.trim()) return;
    setAramaAktif(true);
  };

  return (
    <div className="space-y-4">
      {/* Seçilenler */}
      <div>
        <div className="mb-2 text-sm font-medium">
          Seçilen beceriler
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            ({value.length}) — Detail&apos;in &quot;Ready&quot; olması için en az bir tane gerekir
          </span>
        </div>
        {value.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            Henüz beceri seçilmedi. Aşağıdan arayabilir veya önerilenlerden ekleyebilirsiniz.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {value.map((s) => (
              <Badge
                key={s.skillId || `ozel-${s.title}`}
                variant={s.skillId ? 'secondary' : 'outline'}
                className="gap-1 py-1"
              >
                {s.title}
                <span className="text-[10px] uppercase opacity-60">{s.languageCode}</span>
                {!s.skillId && <span className="text-[10px] opacity-60">özel</span>}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => cikar(s)}
                    className="ml-1 rounded-sm px-1 hover:bg-destructive/10"
                    aria-label={`${s.title} becerisini kaldır`}
                  >
                    ×
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Arama */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={term}
            onChange={(e) => { setTerm(e.target.value); setAramaAktif(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ara(); } }}
            placeholder="Beceri ara (ör. aviation, first aid)"
            disabled={disabled}
          />
          <div className="flex overflow-hidden rounded-md border">
            {['tr', 'en'].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => { setLang(l); setAramaAktif(false); }}
                disabled={disabled}
                className={`px-3 text-sm uppercase ${
                  lang === l ? 'bg-primary text-primary-foreground' : 'bg-background'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Button type="button" onClick={ara} disabled={disabled || !term.trim()}>
            Ara
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Sonuçlar uluslararası beceri kütüphanesinden gelir; Türkçe karşılıklar otomatik
          çeviridir ve arama düz metin eşleşmesi yapar — alakasız sonuçlar çıkabilir.
          Türkçe aramada sonuç bulamazsanız İngilizce deneyin, indeks daha zengindir.
        </p>

        {isFetching && <p className="text-sm text-muted-foreground">Aranıyor…</p>}
        {error && (
          <p className="text-sm text-destructive">{getApiErrorMessage(error)}</p>
        )}
        {aramaAktif && !isFetching && !error && sonuclar.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Sonuç yok. Diğer dili deneyin veya aşağıdan özel beceri ekleyin.
          </p>
        )}

        {sonuclar.length > 0 && (
          <ul className="max-h-56 divide-y overflow-y-auto rounded-md border">
            {sonuclar.map((s) => {
              const secili = value.some((v) => ayniMi(v, s));
              return (
                <li key={s.skillId} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-sm">{s.title}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant={secili ? 'ghost' : 'outline'}
                    onClick={() => ekle(s)}
                    disabled={disabled || secili}
                  >
                    {secili ? 'Eklendi' : 'Ekle'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Özel beceri */}
      <div>
        <div className="mb-1 text-sm font-medium">Özel beceri ekle</div>
        <p className="mb-2 text-xs text-muted-foreground">
          Kütüphanede karşılığı olmayan beceriler (ör. &quot;Kabin Güvenliği&quot;) buradan
          eklenir. Rozette görünür, ancak standart taksonomiye bağlı olmaz.
        </p>
        <div className="flex gap-2">
          <Input
            value={ozelBaslik}
            onChange={(e) => setOzelBaslik(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ozelEkle(); } }}
            placeholder="Beceri adı"
            disabled={disabled}
          />
          <Button type="button" variant="outline" onClick={ozelEkle} disabled={disabled || !ozelBaslik.trim()}>
            Ekle
          </Button>
        </div>
      </div>

      {/* Öneriler */}
      <div>
        <div className="mb-2 text-sm font-medium">Önerilen beceriler</div>
        <div className="flex flex-wrap gap-2">
          {ONERILEN_BECERILER.map((s) => {
            const secili = value.some((v) => ayniMi(v, s));
            return (
              <Button
                key={s.skillId}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => ekle(s)}
                disabled={disabled || secili}
              >
                {secili ? '✓ ' : '+ '}{s.title}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
